import React, { useState, useRef, useEffect } from "react";
import Tabs from "./tabs";
import { Cheveron, Guided, Logo, PCLOGO } from "../../constants";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { setAccessToken, setCurrentUser, switchRole } from "../../store/slices/formconfig";
import "./header.css";
import { useNavigate } from "react-router-dom";
import { ROLES, VIEWER_ROLES } from "../../constants/textconstants";

interface HeaderProps {
  triggerTour: () => void;
}

const Header: React.FC<HeaderProps> = ({triggerTour}) => {
  const { instance } = useMsal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showGeoModal, setShowGeoModal] = useState(false);
  const [selectedGeoTab, setSelectedGeoTab] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.formConfig.user);
  const accessRoles = useSelector((state: any) => state.formConfig.accessRoles);
  const handleLogout = async () => {
    try {
      // Clear active account and local token state first
      instance.setActiveAccount(null);
      dispatch(setAccessToken(null));
      // Clear user in redux store as well
      dispatch(setCurrentUser({}));
      // Clear all browser storage and cache
      localStorage.clear();
      sessionStorage.clear();
      // Clear all cookies
      document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      // Clear Cache Storage if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      await instance.logoutPopup({
        postLogoutRedirectUri: "/login",
        mainWindowRedirectUri: "/unautherized"
      });

      navigate('/login');
    } catch (error) {
      // Fallback to redirect logout if popup fails
      try {
        await instance.logoutRedirect({
          postLogoutRedirectUri: "/login",
        });
      } catch (redirectErr) {
        console.error('Logout failed', redirectErr);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <div className="header">
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
        <img src={PCLOGO} height={30} style={{ marginRight: 20 }} alt="" />
        <span style={{ display: 'block', marginLeft: 12, width: 2, background: 'rgba(0, 0, 0, 0.60)', height: 28 }}></span>
        <span className='app-name'>Principal Certificate Management System</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
        <Tabs />

       { !(userData?.role === VIEWER_ROLES.CL || userData?.role === VIEWER_ROLES.VIEWER) && 
       <div className='guided-cont' onClick={triggerTour}>
          <img src={Guided} height={32} style={{marginRight:16}}/>
          <span className="guided-tour-text">Guided Tour</span>
        </div>}
        <div className="profile-container" ref={dropdownRef} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <div
            role="button"
            aria-label={`User profile ${userData?.username || ''}`}
            title={userData?.username || ''}
            
            className="profile-initials"
            style={{ marginLeft: 24 }}
          >
            {(() => {
              const name = userData?.username || '';
              const parts = name.trim().split(/\s+/).filter(Boolean);
              let initials = '';
              if (parts.length === 0) {
                initials = '?';
              } else if (parts.length === 1) {
                // If only first name present, take first two characters
                initials = parts[0].substring(0, 2).toUpperCase();
              } else {
                // Take first letter of first and second (middle) name
                initials = (parts[0][0] + (parts[1][0] || '')).toUpperCase();
              }
              return initials;
            })()}
          </div>


          {/* Render user initials inside a circle instead of a static image */}
          {(() => {
            const loc = userData?.mappedLocations && userData?.mappedLocations.length > 0 ? userData.mappedLocations[0] : null;
            if(userData?.role === VIEWER_ROLES.CL || userData?.role === VIEWER_ROLES.VIEWER){
              return (
              <div>
                <span className='profile-name'>{userData.username}</span>
                <div className="profile-capsule" style={{marginTop:-7}}>
                  <span className="profile-capsule-label">{userData?.role === VIEWER_ROLES.CL?"Country Lead":"Viewer"} </span>
                 
                </div>
              </div>
            );
            }
            if (!loc) return null;
            const role = userData?.role;
            let label = '';
            let value = '';
           
            if (role === ROLES.LICENSE_MANAGER || role === 'LICENSE_MANAGER') {
              label = 'unit';
              value = [...new Set(userData.mappedLocations.map((l: any) => l.unit_name).filter(Boolean))].join(', ');
            } else if (role === ROLES.APPROVER || role === 'APPROVER') {
              label = 'squad';
              value = [...new Set(userData.mappedLocations.map((l: any) => l.squad_name).filter(Boolean))].join(', ');
            } else if (role === ROLES.ISSUER || role === 'ISSUER') {
              label = 'territory';
              value = [...new Set(userData.mappedLocations.map((l: any) => l.territory_name).filter(Boolean))].join(', ');
            }

            if (!label || !value) return null;
            return (
              <div style={{ display: 'flex',
                            gap: 14,
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.08)',
                            padding: '10px 20px',
                            borderRadius: '26px'}}>
                <span className='profile-name'>{userData.username} : </span>
                <div className="profile-capsule" style={{marginTop:-5}} title={`${label}: ${value}`}>
                  <span className="profile-capsule-label" >{role?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}</span>
                  {/* <span className="profile-capsule-value">{value}</span> */}
                </div>
              </div>
            );
          })()}
          <img src={Cheveron} height={35}/>

          {isDropdownOpen && (
            <div className="profile-dropdown" style={{ position: 'absolute', right: 50, top: 58, width:185 }}>
              {accessRoles && accessRoles.length > 1 && (
                <>
                  <span style={{ display: 'block', padding: '6px 16px', fontSize: 11, color: 'rgba(0,0,0,0.5)', fontWeight: 600, letterSpacing: '0.5px' }}>SWITCH ROLE</span>
                  {accessRoles.map((access: any, index: number) => (
                    <>
                    <span
                      key={index}
                      style={{
                        display: 'block',
                        background: userData?.role === access.role ? '#e8f5e9' : '#fff',
                        borderBottom: '1px solid #efefef',
                        
                        padding: '6px 16px',
                        fontSize: 12,
                        cursor: 'pointer',
                        
                        fontWeight: userData?.role === access.role ? 600 : 400,
                      }}
                      onClick={() => {
                        dispatch(switchRole(index));
                        setIsDropdownOpen(false);
                        navigate('/');
                      }}
                    >
                       {/* <span style={{display:'block', fontSize:10, color:'rgba(0,0,0,0.5)', marginTop:2}}>
                      {access.mappedLocations && access.mappedLocations.length > 0 && (
                        access.role == ROLES.ISSUER? 
                      access.mappedLocations.map((loc: any) => loc.territory_name).filter(Boolean).join(', ') : 
                      access.role==ROLES.APPROVER? access.mappedLocations.map((loc: any) => loc.squad_name).filter(Boolean).join(', ') : 
                      access.role==ROLES.LICENSE_MANAGER? access.mappedLocations.map((loc: any) => loc.unit_name).filter(Boolean).join(', ') : '')}
                    </span> */}
                      {access.role}
                      {userData?.role === access.role && ' ✓'}
                     
                    </span>
                    </>
                  ))}
                  {/* <div style={{ borderTop: '1px solid #efefef', margin: '4px 0' }} /> */}
                </>
              )}


              {accessRoles && accessRoles.length > 0 && (
                <span
                  style={{ display: 'block', background: '#fff', borderBottom: '1px solid #efefef', padding: '6px 16px', fontSize: 12, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGeoModal(true);
                    setIsDropdownOpen(false);
                    setSelectedGeoTab(0);
                  }}
                >
                  Assigned Geographies &rsaquo;
                </span>
              )}
              <span style={{ display: 'block', background: '#fff',  borderRadius: 6, padding: '6px 16px', fontSize: 12 }} onClick={handleLogout}>Logout</span>
            </div>
          )}
        </div>
      </div>

      {/* Assigned Geographies Modal */}
      {showGeoModal && accessRoles && accessRoles.length > 0 && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', zIndex: 1000,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
          }}
          onClick={() => setShowGeoModal(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 10, padding: '24px 32px',
              minWidth: 420, maxWidth: 560, maxHeight: '80vh', overflowY: 'auto',
              position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Assigned Geographies</h3>
              <span
                style={{ cursor: 'pointer', fontSize: 22, color: '#666', lineHeight: 1 }}
                onClick={() => setShowGeoModal(false)}
              >
                &times;
              </span>
            </div>

            {/* Role tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {accessRoles.map((access: any, index: number) => (
                <button
                  key={index}
                  style={{
                    padding: '6px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                    border: selectedGeoTab === index ? '2px solid #1976d2' : '1px solid #ccc',
                    background: selectedGeoTab === index ? '#e3f2fd' : '#fff',
                    color: selectedGeoTab === index ? '#1976d2' : '#333',
                    fontWeight: selectedGeoTab === index ? 600 : 400,
                  }}
                  onClick={() => setSelectedGeoTab(index)}
                >
                  {access.role?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
                </button>
              ))}
            </div>

            {/* Location details for selected tab */}
            {(() => {
              const selected = accessRoles[selectedGeoTab];
              if (!selected || !selected.mappedLocations) return null;
              const locs = selected.mappedLocations;
              const units = [...new Set(locs.map((l: any) => l.unit_name).filter(Boolean))];
              const squads = [...new Set(locs.map((l: any) => l.squad_name).filter(Boolean))];
              const territories = [...new Set(locs.map((l: any) => l.territory_name).filter(Boolean))];

              return (
                <div style={{ fontSize: 14 }}>
                  {units.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Units:</div>
                      <div style={{ color: '#444' }}>{units.join(', ')}</div>
                    </div>
                  )}
                  {squads.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Squads:</div>
                      <div style={{ color: '#444' }}>{squads.join(', ')}</div>
                    </div>
                  )}
                  {territories.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Territory:</div>
                      <div style={{ color: '#444' }}>{territories.join(', ')}</div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
